import type { LucideIcon } from "lucide-react";
import { ArrowLeftRight, ClipboardList, Layers, MessageCircle } from "lucide-react";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { StaggerInView, StaggerInViewItem } from "../../tools/Animations";
import { HealthSection } from "../HealthSection";
import { getLocalized, HealthLang, LocalizedString } from "../healthTypes";
import {
    healthSectionEyebrow,
    healthWhyDoctorAccent,
    healthWhyDoctorCard,
    healthWhyDoctorCardBody,
    healthWhyDoctorCardContent,
    healthWhyDoctorCardDivider,
    healthWhyDoctorCardIconCol,
    healthWhyDoctorCardIndex,
    healthWhyDoctorCardTitle,
    healthWhyDoctorGrid,
    healthWhyDoctorHeader,
    healthWhyDoctorHeaderStack,
    healthWhyDoctorIconGlyph,
    healthWhyDoctorIconShell,
    healthWhyDoctorIntro,
    healthWhyDoctorSectionWrap,
    healthWhyDoctorTitle,
} from "../healthVisualStyles";

const WHY_NURSE_ICONS: LucideIcon[] = [
    ClipboardList,
    MessageCircle,
    ArrowLeftRight,
    Layers,
];
const WHY_NURSE_ICON_STROKE = 1.75;

interface HealthWhyNurseSectionProps {
    data: DataKeys;
}

export function HealthWhyNurseSection({ data }: HealthWhyNurseSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const eyebrow = getLocalized(data.eyebrow, hLang);
    const title = getLocalized(data.title, hLang);
    const intro = getLocalized(data.intro, hLang);
    const cards = (data.cards as DataKeys[]) ?? [];
    const sectionId = "why-nurse";

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact
            style={{ backgroundColor: "#ffffff" }}
        >
            <div className={healthWhyDoctorSectionWrap}>
                <header className={healthWhyDoctorHeader}>
                    <div className={healthWhyDoctorHeaderStack}>
                        <p className={healthSectionEyebrow}>{eyebrow}</p>
                        <span className={healthWhyDoctorAccent} aria-hidden="true" />
                        <h2 id={`${sectionId}-title`} className={healthWhyDoctorTitle}>
                            {title}
                        </h2>
                        <p className={healthWhyDoctorIntro}>{intro}</p>
                    </div>
                </header>
                <StaggerInView className={healthWhyDoctorGrid}>
                    {cards.map((card, index) => {
                        const Icon = WHY_NURSE_ICONS[index] ?? ClipboardList;
                        const indexLabel = String(index + 1).padStart(2, "0");

                        return (
                            <StaggerInViewItem key={index} direction="bottom">
                            <article className={healthWhyDoctorCard}>
                                <div className={healthWhyDoctorCardIconCol}>
                                    <div className={healthWhyDoctorIconShell}>
                                        <Icon
                                            className={healthWhyDoctorIconGlyph}
                                            strokeWidth={WHY_NURSE_ICON_STROKE}
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <div
                                    className={healthWhyDoctorCardDivider}
                                    aria-hidden="true"
                                />
                                <div className={healthWhyDoctorCardContent}>
                                    <p className={healthWhyDoctorCardIndex}>
                                        {indexLabel}
                                    </p>
                                    <h3 className={healthWhyDoctorCardTitle}>
                                        {getLocalized(
                                            card.title as LocalizedString,
                                            hLang
                                        )}
                                    </h3>
                                    <p className={healthWhyDoctorCardBody}>
                                        {getLocalized(
                                            card.text as LocalizedString,
                                            hLang
                                        )}
                                    </p>
                                </div>
                            </article>
                            </StaggerInViewItem>
                        );
                    })}
                </StaggerInView>
            </div>
        </HealthSection>
    );
}
